import React, { useEffect, useState } from 'react';

import {
  Skeleton,
} from '@mui/material';

import LessonDetails from '../../../../../components/LessonDetails';
import SkeletonStack from '../../../../../components/SkeletonStack';
import StudentRow from '../../../../../components/StudentRow';
import {
  useGetLessonEnrolmentQuery,
  useGetLessonQuery,
  useMarkRollMutation,
  useUnmarkRollMutation,
  useUpdateLessonEnrolmentMutation,
  useUpdateLessonMutation,
} from '../../../../../services/classes';
import { Lesson, LessonEnrolment } from '../../../../../services/classes/types';
import { useGetCoursesQuery } from '../../../../../services/courses';
import { useGetStaffQuery } from '../../../../../services/staff';
import { useGetStudentQuery } from '../../../../../services/students';
import { AttendanceType, EnrolmentType } from '../../../../../types/common';
import {
  getLessonClass,
  getLessonEnrolmentClass,
  getLessonEnrolmentLessonId,
  getLessonEnrolmentSubject,
  getLessonSubject,
} from '../../../../../utils/common/classes';
import useError from '../../../../../utils/react/hooks/useError';

interface LessonStudentRowProps {
  studentId: string,
  lesson: Lesson,
}

const LessonStudentRow = ({ studentId,
  lesson }: LessonStudentRowProps) => {
  const { data: student, isLoading } = useGetStudentQuery({ studentId });
  const [updateLessonEnrolment] = useUpdateLessonEnrolmentMutation();

  const subject = getLessonSubject(lesson);
  const classId = getLessonClass(lesson);

  const { data: lessonEnrolment,
    isLoading: isLessonEnrolmentLoading } = useGetLessonEnrolmentQuery({
    subject,
    classId,
    lesson: lesson.lesson,
    studentId,
  });

  const handleMarkStudent = (id: string, present: boolean) => {
    updateLessonEnrolment({
      subject,
      classId,
      lesson: lesson.lesson,
      studentId: id,
      newAttributes: {
        present,
      },
    });
  };

  if (isLoading || isLessonEnrolmentLoading) {
    return (
      <Skeleton />
    );
  }

  if (student?.data && lessonEnrolment?.data) {
    return (
      <StudentRow
        student={student.data}
        studentDetailsHref={`/admin/student/${student.data.studentId}`}
        editable={!lesson.rollMarked}
        isPresent={lessonEnrolment.data.present}
        isMakeup={lessonEnrolment.data.enrolmentMethod === EnrolmentType.Makeup}
        isOnline={lessonEnrolment.data.attendanceType === AttendanceType.Online}
        onMark={handleMarkStudent}
        makeupHref={`/roll/${subject}/${classId}/${encodeURIComponent(lesson.lesson)}/reschedule/${studentId}`}
      />
    );
  }
  return (
    <div />
  );
};
interface StudentLessonDetailsProps {
  selectedLesson: LessonEnrolment;
  onClose: () => void;
}

const StudentLessonDetails = ({
  selectedLesson,
  onClose = () => {},
}: StudentLessonDetailsProps) => {
  const { data: courses, isLoading: isCoursesLoading } = useGetCoursesQuery({});

  const subject = getLessonEnrolmentSubject(selectedLesson);
  const classId = getLessonEnrolmentClass(selectedLesson);
  const lessonId = getLessonEnrolmentLessonId(selectedLesson);

  const { data: fetchedLesson,
    isFetching: isLessonLoading } = useGetLessonQuery({
    subject, classId, lesson: lessonId,
  });
  const { data: teacher, isLoading: isTeacherLoading } = useGetStaffQuery({
    staffId: fetchedLesson?.data.teacher ?? '',
  }, { skip: !fetchedLesson });

  const [lesson, setLesson] = useState<Lesson>();

  useEffect(() => {
    if (fetchedLesson) {
      setLesson(fetchedLesson.data);
    }
  }, [fetchedLesson]);

  const [updateLesson] = useUpdateLessonMutation();
  const [markRoll, { isLoading: isMarkRollLoading,
    isError: isMarkRollError,
    error: markRollError }] = useMarkRollMutation();
  const [unmarkRoll, { isLoading: isUnmarkRollLoading,
    isError: isUnmarkRollError,
    error: unmarkRollError }] = useUnmarkRollMutation();

  const handleSubmitRoll = () => {
    if (!lesson?.rollMarked) {
      markRoll({
        subject,
        classId,
        lesson: lessonId,
      });
    } else {
      unmarkRoll({
        subject,
        classId,
        lesson: lessonId,
      });
    }
  };

  useError(isMarkRollError, markRollError);
  useError(isUnmarkRollError, unmarkRollError);

  if (isLessonLoading || isCoursesLoading || isTeacherLoading || !lesson) {
    return (
      <SkeletonStack />
    );
  }

  return (
    <LessonDetails
      lesson={lesson}
      classDetailsHref={`/admin/class/${getLessonSubject(lesson)}/${getLessonClass(lesson)}`}
      courses={courses?.data ?? []}
      teacher={teacher?.data}
      onClose={onClose}
      toggleRoll={handleSubmitRoll}
      updateLesson={(newAttributes) => updateLesson(
        {
          subject, classId, lesson: lessonId, newAttributes,
        },
      )}
      isRollLoading={isMarkRollLoading || isUnmarkRollLoading}
      renderStudentRow={(studentId) => (
        <LessonStudentRow
          key={studentId}
          studentId={studentId}
          lesson={lesson}
        />
      )}
    />
  );
};

export default StudentLessonDetails;
